// Pure helpers and constants for BulkUploadZoneModal.
//
// Kept out of the SFC so they can be unit-tested directly: this is the file
// validation, date normalisation and row-classification logic that decides what
// a bulk upload sends to the backend, and it is locale-sensitive enough that it
// must be pinned by tests rather than by manual QA on one developer's machine.

// "Facility Name" excluded — upload is always scoped to current facility
export const FULL_TEMPLATE_COLUMNS = [
  "Unit #",
  "Unit Type",
  "Unit Size",
  "Zone Name",
  "First Name",
  "Last Name",
  "Phone (Primary)",
  "Phone (Secondary)",
  "Email",
  "Move-In Date",
];

export const ZONE_TEMPLATE_COLUMNS = ["Unit #", "Zone Name"];

// Full mode needs only Unit # — a sheet of units with no zones is valid and each
// unit is saved Unassigned. Zone mode maps units onto zones, so it needs both.
export const REQUIRED_COLUMNS = ["Unit #"];
export const ZONE_MODE_REQUIRED_COLUMNS = ["Unit #", "Zone Name"];

// Keep the byte limit and the label in lockstep — every piece of user-facing
// copy reads MAX_FILE_LABEL so the two can never drift apart.
export const MAX_FILE_MB = 2;
export const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;
export const MAX_FILE_LABEL = `${MAX_FILE_MB} MB`;

export const MAX_UPLOAD_ROWS = 1000;

// Rows that cannot be saved: hard validation errors, and live units the backend
// will refuse to modify.
export const BLOCKED_STATUSES = ["Error", "Skipped"];

export const MODES = ["full", "zone"];

// Review-table sort order: the rows an operator must act on come first, then the
// ones worth a glance, then the ones that need no attention at all. Used as the
// default sort and as the Status column's comparator, so alphabetical ordering
// ("AutoFixed" before "Error") never surfaces.
export const STATUS_RANK = {
  Error: 0,
  Warning: 1,
  Skipped: 2,
  AutoFixed: 3,
  Valid: 4,
};

export function statusRank(status) {
  // Unknown statuses sort last rather than throwing off the comparator.
  return STATUS_RANK[status] ?? Number.MAX_SAFE_INTEGER;
}

// Does a preview row belong to the given summary-tile filter? Kept total — an
// unrecognised key shows everything rather than an empty table.
export function rowMatchesFilter(row, filterKey) {
  switch (filterKey) {
    case "error":
      return row.status === "Error";
    case "warning":
      return row.status === "Warning";
    case "skipped":
      return row.status === "Skipped";
    case "zone":
      return row.included && !!row.zone;
    case "tenant":
      return row.included && !!row.tenant;
    default:
      return true;
  }
}

export function normalizeCellText(value) {
  return String(value ?? "")
    .normalize("NFC")
    .replace(/[​‌‍⁠﻿]/g, "")
    .replace(/[   ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function levenshtein(a, b) {
  const m = a.length,
    n = b.length;
  const dp = Array.from({ length: m + 1 }, (_, i) =>
    Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
  );
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] =
        a[i - 1] === b[j - 1]
          ? dp[i - 1][j - 1]
          : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
  return dp[m][n];
}

export function strSimilarity(a, b) {
  const la = a.toLowerCase(),
    lb = b.toLowerCase();
  if (la === lb) return 1;
  const maxLen = Math.max(la.length, lb.length);
  return maxLen === 0 ? 1 : (maxLen - levenshtein(la, lb)) / maxLen;
}

// Closest existing zone name for a downcased candidate.
// `zoneNameMap` is downcased name → display name.
export function bestZoneMatch(zoneLower, zoneNameMap) {
  let name = null;
  let score = 0;
  zoneNameMap.forEach((displayName, zl) => {
    const s = strSimilarity(zoneLower, zl);
    if (s > score) {
      score = s;
      name = displayName;
    }
  });
  return { name, score };
}

export function normalizePhone(raw) {
  if (!raw) return raw;
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 10)
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits[0] === "1")
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  return raw;
}

// Dates are parsed as STRINGS only — never via `new Date()`.
//
// `new Date("07/20/2026")` builds LOCAL midnight, so `.toISOString()` shifts the
// day back for anyone east of UTC: a US machine reads 2026-07-20 while an IST or
// EET machine reads 2026-07-19, silently. `new Date()` is also engine-dependent
// for non-ISO input. Both are avoided here, so the result is identical on every
// machine regardless of OS locale or timezone.
const ISO_DATE_RE = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;
// StorageDefender is a US product, so M/D/Y is the assumed order for all three
// separators. A first component above 12 cannot be a month and is rejected
// rather than silently re-read as D/M/Y.
const US_DATE_RE = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/;

export function pad2(value) {
  return String(value).padStart(2, "0");
}

export function isRealCalendarDate(year, month, day) {
  if (month < 1 || month > 12 || day < 1) return false;
  const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  const lengths = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return day <= lengths[month - 1];
}

// Returns "YYYY-MM-DD", or null when the value cannot be read unambiguously.
export function normalizeDate(raw) {
  const text = String(raw ?? "").trim();
  if (!text) return null;

  const iso = text.match(ISO_DATE_RE);
  if (iso) {
    const [year, month, day] = [+iso[1], +iso[2], +iso[3]];
    if (!isRealCalendarDate(year, month, day)) return null;
    return `${iso[1]}-${pad2(month)}-${pad2(day)}`;
  }

  const us = text.match(US_DATE_RE);
  if (us) {
    const [month, day, year] = [+us[1], +us[2], +us[3]];
    if (!isRealCalendarDate(year, month, day)) return null;
    return `${us[3]}-${pad2(month)}-${pad2(day)}`;
  }

  return null;
}

// An Excel date-typed cell arrives as a real Date built from the sheet's serial
// number. Read its LOCAL parts — the serial has no timezone, so converting is
// what would corrupt it.
export function excelDateToIso(value) {
  if (!(value instanceof Date) || isNaN(value.getTime())) return null;
  return `${value.getFullYear()}-${pad2(value.getMonth() + 1)}-${pad2(
    value.getDate()
  )}`;
}

export function isValidPhone(str) {
  if (!str) return true;
  return /^[\d\s\-()+.]{7,20}$/.test(str);
}

// Mirrors Organizations.parse_unit_size/1 on the backend — accepts 10x10, 10X10,
// 10 x 10, 10×10 and 10*10.
export function isValidUnitSize(str) {
  if (!str) return true;
  return /^\s*\d+(\.\d+)?\s*[x×*]\s*\d+(\.\d+)?\s*$/i.test(str);
}

export function isValidEmail(str) {
  if (!str) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str);
}
